// SSE de-duplication ring — carved VERBATIM from
// `src/features/agent-events/event-dedup.ts` (ARCFE-1102 · 4.4), which is now a
// named re-export shim. Class body byte-identical; only this header is new.
//
// WHY IT LANDED HERE rather than at `@heidi/utils` or `@heidi/runtime/events`,
// which were the other two options recorded on `agents/harness`. It is not a
// shared utility by measurement: three production importers, all agents-lane.
// `@heidi/utils` would declare a transport-level dedup ring with a
// `DedupStorage` port to be a generic platform utility on the strength of zero
// non-agents importers, and `@heidi/runtime/events` is the
// `DomainEventDescriptor` emit/subscribe registry — a different event mechanism
// that would end up sharing one name with this one.
//
// It travels WITH `user-events-client.ts` in one slice, which is the part that
// mattered: moving it alone would have unblocked the two harness stream hooks
// BEFORE user-wide events were separated, fusing run scope to user scope rather
// than splitting them — the opposite of 4.6's stated precondition.
const DEFAULT_MAX_ENTRIES = 50;

export interface DedupStorage {
  load(): readonly string[];
  save(ids: readonly string[]): void;
}

// In-memory ring used to drop duplicate SSE deliveries within a single
// session (replay races, transient reconnects). Cross-reload dedup is
// owned by the BE: ``find_resolved_event_ids`` filters already-handled
// events out of the SSE backfill, so the FE does not persist seen ids.
export class EventDedup {
  private readonly seen = new Set<string>();
  private readonly maxEntries: number;
  private readonly storage?: DedupStorage;

  constructor(maxEntries = DEFAULT_MAX_ENTRIES, storage?: DedupStorage) {
    this.maxEntries = maxEntries;
    this.storage = storage;
    if (storage) {
      for (const id of storage.load()) {
        this.seen.add(id);
      }
      this.evict();
    }
  }

  markSeen(eventId: string): boolean {
    if (this.seen.has(eventId)) return false;
    this.seen.add(eventId);
    this.evict();
    this.storage?.save([...this.seen]);
    return true;
  }

  private evict(): void {
    while (this.seen.size > this.maxEntries) {
      const oldest = this.seen.values().next().value;
      if (oldest === undefined) return;
      this.seen.delete(oldest);
    }
  }
}
